<template>
  <n-modal-background :closeOnEscape="false" :closeOnOutside="false" ref="bg" coverSnackbars @close="$emit('dismiss')">
    <n-modal-body class="getting-started-modal" width="m">
      <div class="main">
        <div class="content">
          <div class="messages">
            <header>{{ $t('framework.welcomeToNotch') }}</header>
            <p class="subtitle">
              {{
                isDesktopViewport
                  ? $t('framework.welcomeToNotchDescription')
                  : $t('framework.welcomeToNotchDescriptionMobile')
              }}
            </p>
          </div>
          <template v-if="isDesktopViewport">
            <p class="features-title">{{ $t('onboarding.highlightedFeatures') }}</p>
            <ul class="plan-features">
              <li class="check-item" v-for="(feature, index) in highlightedFeatures" :key="index">
                <n-icon class="icon-space" icon="check-circle" size="s" />
                <p class="feature-name">{{ feature.featureName }}</p>
                <p class="feature-description">{{ feature.featureDescription }}</p>
              </li>
            </ul>
          </template>
          <footer>
            <n-button variant="tertiary" :label="$t('onboarding.dismiss')" @click="dismiss" />
            <n-button :label="$t('onboarding.createYourFirstSpace')" icon="arrow-right" @click="openTemplateManager" />
          </footer>
        </div>
        <div class="video" v-if="isDesktopViewport">
          <video
            ref="welcomeVideo"
            :src="welcomeVideoLink"
            :poster="gettingStartedImageLink"
            autoplay
            muted
            loop
            playsinline
          />
        </div>
      </div>
    </n-modal-body>
  </n-modal-background>
</template>

<script lang="ts">
import { Component, Ref, Vue } from 'vue-property-decorator'
import { trackOnboardingFlow1 } from '@/util/segment-tracking'
import { useUserStore } from '@/modules/user/stores/UserStore'
import { mapState, mapStores } from 'pinia'
import User from '@/modules/user/models/User'
import { isViewportAtLeast, subscribeToViewportWidthChange } from '@/util/dom-utils'
import { SpacePageTemplateStore, useSpacePageTemplateStore } from '@/modules/space/stores/SpacePageTemplateStore'
import { TemplateUsageContext } from '@/modules/spacepagetemplates/models/SpacePageTemplate'
import { OnboardingStore, useOnboardingStore } from '@/modules/global/stores/OnboardingStore'
import { OnboardingStage } from '@/modules/global/onboardingStorage'

const WELCOME_VIDEO_BREAKPOINT = 768

@Component({
  computed: {
    ...mapStores(useUserStore, useSpacePageTemplateStore, useOnboardingStore),
    ...mapState(useUserStore, ['currentUser'])
  }
})
export default class GettingStartedModal extends Vue {
  @Ref('welcomeVideo') readonly welcomeVideoEl?: HTMLVideoElement
  @Ref('bg') readonly backgroundRef?: { close: () => void }

  private currentUser!: User

  private gettingStartedImageLink = `${import.meta.env.VITE_NOTCH_STATIC_STORAGE}/getting-started-illustration.png`
  private welcomeVideoLink = `${import.meta.env.VITE_NOTCH_STATIC_STORAGE}/welcome-video.mp4`

  private isDesktopViewport = isViewportAtLeast(WELCOME_VIDEO_BREAKPOINT)
  private unsubscribeFromViewportChange: (() => void) | null = null

  spacePageTemplateStore!: SpacePageTemplateStore
  onboardingStore!: OnboardingStore

  get highlightedFeatures() {
    const features = Object.keys(this.$t('onboarding.features'))

    return features.map(feature => ({
      featureName: this.$t(`onboarding.features.${feature}.title`),
      featureDescription: this.$t(`onboarding.features.${feature}.subtitle`)
    }))
  }

  mounted() {
    this.unsubscribeFromViewportChange = subscribeToViewportWidthChange(WELCOME_VIDEO_BREAKPOINT, matches => {
      this.isDesktopViewport = matches
    })
  }

  beforeDestroy() {
    this.unsubscribeFromViewportChange?.()
    this.pauseVideo()
  }

  pauseVideo() {
    this.welcomeVideoEl?.pause()
  }

  dismiss() {
    this.pauseVideo()
    this.backgroundRef?.close()
  }

  openTemplateManager() {
    this.pauseVideo()
    // NS-310: persist the stage before opening the picker, so a reload comes back to the picker
    // rather than to this modal. The parent renders this modal off the stage, so saving it also
    // closes this modal - the emit stays for any other listener.
    this.onboardingStore.saveStage(OnboardingStage.TEMPLATE_SELECTION)
    this.$emit('close')
    this.spacePageTemplateStore.setTemplateManagerModal(true, TemplateUsageContext.CREATE_SPACE_SELECT_SPACE_LIST)
    trackOnboardingFlow1()
  }
}
</script>

<style scoped lang="scss">
@use '@/assets/styles' as *;

.getting-started-modal {
  display: flex;
  flex-direction: row;

  .main {
    display: flex;
    flex-direction: row;
    width: 100%;
  }

  .content {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: $notch-space-8x;
  }

  .video {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    video {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    }
  }

  header {
    @include notch-text-heading-semibold-L;
    margin-bottom: $notch-space-4x;
  }

  .subtitle {
    @include notch-text-paragraph-regular-S;
    color: $notch-color-neutral-grey-900;
    margin-bottom: $notch-space-12x;
  }

  footer {
    display: flex;
    justify-content: flex-start;
    width: 100%;
    margin-top: auto;
  }
}

.features-title {
  @include notch-text-paragraph-semibold-M;
  color: $notch-color-neutral-grey-900;
}

.plan-features {
  list-style: none;
  text-align: left;
  li {
    margin-bottom: $notch-space-3x;
  }
}

.check-item {
  @include notch-text-label-light-M;
  color: $notch-color-neutral-grey-900;
  display: flex;
  margin-top: $notch-space-2x;
  gap: $notch-space-1x;
}

.feature-name {
  @include notch-text-label-medium-M;
  color: $notch-color-neutral-grey-900;
}

.feature-description {
  @include notch-text-label-light-M;
  color: $notch-color-neutral-grey-900;
}
</style>
